import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthHelperService } from './auth-helper.service';
import { CrmType } from 'app/crm-login/crm-login.component';
import { UserSettingsFacade } from 'app/store/user-settings.facade';
import { firstValueFrom, take } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LoginVia, UserSettings } from 'app/global/user-settings.dto';
import { environment } from 'environments/environment';
import { isInIframe, IFRAME_REQUEST_HEADER } from '../interceptors/iframe.interceptor';

@Injectable({
    providedIn: 'root'
})
export class IframeAbleService {
    private isIframeAlbeLogin$ = this._userSettingsFacade$._userSettings$;
    private bypassHttpInterceptor:HttpClient;
    constructor(
        private http: HttpClient,
        private _authHelper: AuthHelperService,
        private _userSettingsFacade$: UserSettingsFacade,
        private _httpBackend:HttpBackend,
    ) {
        this.bypassHttpInterceptor = new HttpClient(this._httpBackend);
    }

    getIframeCSRFToken() {
        const apiUrl = environment.apiUrl;
        const headers = isInIframe() ? {
            [IFRAME_REQUEST_HEADER]: 'true'
        } : {};
        return this.bypassHttpInterceptor.get(`${apiUrl}csrf-iframe-cookie`, {
            headers: new HttpHeaders(headers),
            responseType: 'text',
            withCredentials: true,
        });
    }

    /**
     * Validates the provided token by sending a POST request to the `iframe-able-login` endpoint.
     * 
     * @param token - The token to be validated. If provided, it will be included in the 
     *                `Authorization` header as a Bearer token.
     * 
     * @returns An observable of the HTTP response from the server.
     * 
     * @remarks
     * - The `Authorization` header is conditionally added if a token is provided.
     * - This method includes credentials in the request and expects a JSON response.
     * - Note: This implementation includes a temporary fix to allow connections from localhost.
     */
    validateToken(token: string) {
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`; //TODO: temporary fix while BE will allow to connect from localhost
        }
        return this.getIframeCSRFToken().pipe(
            switchMap(() => this.http.post(`iframe-able-login`, { login_via: CrmType.IFrameAble }, {
                headers: {
                    'accept': `application/json`,
                    ...headers
                }
            }))
        );
    }


    /**
     * Creates a document using the specified template ID.
     *
     * @param templateId - The ID of the template to create the document from.
     * @returns An `Observable` of the HTTP POST response.
     */
    createDocumentViaTemplateId(templateId: number) {
        return this.http.post(`create-document/from-template/${templateId}`, null);
    }

    /**
     * Sets the layout configuration for iframeable login scenarios.
     * 
     * This method checks if the user is logged in through an iframeable session
     * and updates the layout configuration accordingly. If the user is logged in
     * via an iframeable session, the layout is set to a specific configuration.
     * 
     * @returns A promise that resolves to a boolean indicating whether the user
     *          is logged in through an iframeable session.
     */
    async setIframeAbleLayout() {
        const isIframeableLogin = await this.isIframeAbleLoggedIn();
        if (isIframeableLogin) {
            this._authHelper.setLayoutConfig(true, false, false);
        }
        return isIframeableLogin;
    }

    /**
     * Checks if the user is logged in for iframeable sessions.
     *
     * This method retrieves the login status for iframeable sessions
     * from the local storage using the `LocalStorageKeys.IFRAMEABLELOGIN` key.
     *
     * @returns A promise that resolves to the value indicating whether the user
     *          is logged in for iframeable sessions.
     */
    async isIframeAbleLoggedIn() {
        const isIframeableLogin: boolean = await firstValueFrom(
              this.isIframeAlbeLogin$.pipe(
                  take(1),
                  map((userSettings:UserSettings) => {
                      return userSettings?.loginAttempt?.login_via === LoginVia.IFRAMEABLE;
                  })
              )
        );
        return isIframeableLogin;
    }
}